import {
    AppstoreAddOutlined,
    DownOutlined,
    EditOutlined,
    UploadOutlined,
    DownloadOutlined,
    DeleteOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Menu, Space } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { HeaderTabs } from '.';
import { type SdmModuleSpecsFragment } from '../../../../api/fragments';
import { useAccountContext } from '../../../../components/contexts/AccountContextManager';
import { exportSdmSalesPersonCodeMappings } from '../../../../utilities/export';
import { type PermissionValues } from '../../../../utilities/hasPermissions';
import useDeleteModule from '../useDeleteModule';

export type SdmModuleActionProps = {
    module: SdmModuleSpecsFragment;
    setActiveTab: (value: HeaderTabs) => void;
    openSaleForceDrawer: () => void;
    openBankIntegrationDrawer: () => void;
    openImportMappingsModal: () => void;
    permissions: Pick<PermissionValues, 'hasDeletePermission'>;
};

const SdmModuleAction = ({
    module,
    setActiveTab,
    openSaleForceDrawer,
    openBankIntegrationDrawer,
    openImportMappingsModal,
    permissions,
}: SdmModuleActionProps) => {
    const { t } = useTranslation(['sdmModuleDetails']);
    const { token } = useAccountContext();
    const deleteModule = useDeleteModule(module.id);

    const downloadMappings = useCallback(() => {
        exportSdmSalesPersonCodeMappings(token, module.id);
    }, [module.id, token]);

    const { hasDeletePermission } = permissions;

    const items = [
        {
            label: t('sdmModuleDetails:actions.updateSalesForce'),
            key: 'updateSalesForce',
            icon: <EditOutlined />,
            onClick: () => {
                setActiveTab(HeaderTabs.SalesForce);
                openSaleForceDrawer();
            },
        },
        {
            label: t('sdmModuleDetails:actions.createBankIntegrations'),
            key: 'createBankIntegrations',
            icon: <AppstoreAddOutlined />,
            onClick: () => {
                setActiveTab(HeaderTabs.BankIntegrations);
                openBankIntegrationDrawer();
            },
        },
        {
            label: t('sdmModuleDetails:actions.importMappings'),
            key: 'importMappings',
            icon: <UploadOutlined />,
            onClick: () => {
                setActiveTab(HeaderTabs.SalesPersonCodeMappings);
                openImportMappingsModal();
            },
        },
        {
            label: t('sdmModuleDetails:actions.exportMappings'),
            key: 'exportMappings',
            icon: <DownloadOutlined />,
            onClick: () => {
                setActiveTab(HeaderTabs.SalesPersonCodeMappings);
                downloadMappings();
            },
        },
        hasDeletePermission && {
            label: t('sdmModuleDetails:actions.delete'),
            key: 'delete',
            icon: <DeleteOutlined />,
            onClick: deleteModule,
            danger: true,
        },
    ];

    return (
        <Dropdown overlay={<Menu items={items} />}>
            <Button type="primary">
                <Space>
                    <span>{t('sdmModuleDetails:actions.label')}</span>
                    <DownOutlined />
                </Space>
            </Button>
        </Dropdown>
    );
};

export default SdmModuleAction;
